import type { ReactNode } from 'react';
import type { FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { FounderSelfIdentificationFields } from '@/features/startups/components/FounderSelfIdentificationFields';
import { buildSelfIdentificationFacts } from '@/features/startups/self-identification';
import {
  FoundingTeamEditor,
  FoundingTeamMemberSummary,
  type FoundingTeamMemberValues,
} from '@/features/startups/components/FoundingTeamEditor';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';

const S = UI_STRINGS.STARTUP_PROFILE;

/**
 * `control` and `register` are gone since the editor moved to the shared
 * `FoundingTeamEditor`, which is controlled by value/onChange - the member rows
 * are no longer individually registered fields, so nothing here needs either.
 */
interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  setValue?: UseFormSetValue<StartupProfileFormValues>;
  watch?: UseFormWatch<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
  /**
   * `formState.isSubmitted` - whether Save has been pressed at least once.
   * Drives when a member's error is allowed to re-evaluate; see the `onChange`
   * below for why it cannot simply be `true`.
   */
  isSubmitted?: boolean;
}

/** Read-only self-identification answers (UX sheet r113-r115). */
function SelfIdentificationFacts({ data }: { data: StartupProfileData }) {
  const facts = buildSelfIdentificationFacts(data, S.NOT_PROVIDED);
  return (
    <dl className="mt-6 flex flex-col gap-4">
      {facts.map((fact) => (
        <div key={fact.label} className="flex flex-col gap-1">
          <dt>
            <FieldLabel label={fact.label} variant="caps" />
          </dt>
          <dd className="text-navy text-p6">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Founding Team card: repeatable member rows (name/title/linkedin/past-exit)
 * - email and bio excluded, see Global Constraints; the member photo was removed
 * in MYN-175 and the Backend drops the column with it.
 *
 * The editor itself is `features/startups/components/FoundingTeamEditor`,
 * shared with the admin console's profile-correction form (MYN-132). This file
 * is the founder's binding to it: the card chrome, the read-only view, and the
 * React Hook Form wiring. The rules that must not drift between the two
 * surfaces (keep at least one founder, cap at five, saved members are summaries
 * not forms) live in the shared component, not here.
 */
export function FoundingTeamSection({
  mode,
  data,
  setValue,
  watch,
  errors,
  isSubmitted = false,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !setValue || !watch) {
    const team = data.foundingTeam ?? [];
    return (
      <SectionCard title={S.FOUNDING_TEAM} headerRight={headerRight}>
        {team.length === 0 ? (
          <p className="text-slate text-p6">{S.NOT_ADDED}</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {team.map((member, index) => (
              <div key={`${member.name}-${index}`} className="bg-slate/10 rounded-card p-6">
                <FoundingTeamMemberSummary
                  member={{
                    name: member.name,
                    title: member.title,
                    linkedinUrl: member.linkedinUrl ?? undefined,
                    pastExits: member.pastExits ? 'yes' : 'no',
                  }}
                />
              </div>
            ))}
          </div>
        )}
        <SelfIdentificationFacts data={data} />
      </SectionCard>
    );
  }

  const members = (watch('foundingTeam') ?? []) as FoundingTeamMemberValues[];

  return (
    <SectionCard title={S.FOUNDING_TEAM}>
      <FoundingTeamEditor
        members={members}
        memberError={(index) => errors?.foundingTeam?.[index]}
        /*
          `shouldValidate` restores what these rows lost when the editor stopped
          being `register`ed: React Hook Form only re-validates fields it owns,
          so a corrected member field would otherwise keep its error until Save
          was pressed a second time.

          Gated on `isSubmitted` rather than always on, because that is exactly
          what `register` gave them - this form's default `mode: 'onSubmit'`
          plus `reValidateMode: 'onChange'`. Unconditional validation would flag
          a half-typed URL on its first keystroke, which is worse than the bug.

          Today this is insurance rather than an observable fix on THIS surface:
          the only member-level rule is the LinkedIn URL, and this form (unlike
          the admin console's, which sets `noValidate`) lets the browser's own
          `type="url"` check block the submit first, so the zod error is never
          reached. It stops being insurance the moment a member gains a rule
          native validation does not cover.
        */
        onChange={(next) =>
          setValue('foundingTeam', next, { shouldDirty: true, shouldValidate: isSubmitted })
        }
      />
      <div className="mt-6">
        <FounderSelfIdentificationFields
          value={{
            founderFemale: watch('founderFemale'),
            founderUnderrepresented: watch('founderUnderrepresented'),
            founderUnderrepresentedCategories: watch('founderUnderrepresentedCategories') ?? [],
          }}
          onChange={(next) => {
            const options = { shouldDirty: true } as const;
            setValue('founderFemale', next.founderFemale, options);
            setValue('founderUnderrepresented', next.founderUnderrepresented, options);
            setValue(
              'founderUnderrepresentedCategories',
              next.founderUnderrepresentedCategories,
              options,
            );
          }}
        />
      </div>
    </SectionCard>
  );
}
